/**
 * Sexy Hair Events calendar — WEB-6279
 *
 * Vanilla-JS List / Month calendar over the `event` metaobjects emitted by
 * snippets/sexyhair-event-calendar.liquid. Replaces the marketing site's
 * WordPress "The Events Calendar" (Tribe) views — no third-party library, per the
 * shared theme rule.
 *
 * TIMEZONE CONTRACT — read before touching any date code.
 * Each class runs at a physical SalonCentric venue in its own timezone, so every
 * event is displayed in its VENUE'S local wall clock, never the visitor's and never
 * the shop's. `localStart` / `localEnd` are naive wall-clock strings
 * ("2026-08-10T09:00:00") stored verbatim at ingest, and `tzAbbr` ("CDT") is the
 * label. Parsing them with `new Date(str)` would reinterpret them in the VISITOR'S
 * zone and shift every time, so they are parsed field-by-field into a "floating"
 * Date via parseLocal() and only ever read back with the getters that match
 * (getFullYear/getMonth/getDate/getHours). That round-trips exactly in any zone.
 * `startsAt` / `endsAt` are real UTC epochs and are used ONLY for the .ics export.
 */
(function () {
  'use strict';

  var MONTHS = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  var DAYS_SHORT = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
  /** Month grid is Monday-first, matching the live site. */
  var WEEKDAY_INITIALS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
  /** How many events the List view shows per page (live shows a comparable slice). */
  var PAGE_SIZE = 10;
  /** Events shown in a month cell before collapsing into "+ N More". */
  var CELL_LIMIT = 2;

  /**
   * Parse a naive wall-clock string into a floating Date (see TIMEZONE CONTRACT).
   * Deliberately does NOT use `new Date(string)` — that applies the visitor's zone.
   */
  function parseLocal(value) {
    if (!value) return null;
    var m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/.exec(value);
    if (!m) return null;
    return new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], 0, 0);
  }

  function startOfDay(date) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  function sameDay(a, b) {
    return (
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate()
    );
  }

  /** "9:00 am" — lowercase meridiem, matching the live site. */
  function formatTime(date) {
    var hours = date.getHours();
    var minutes = date.getMinutes();
    var meridiem = hours >= 12 ? 'pm' : 'am';
    var display = hours % 12;
    if (display === 0) display = 12;
    return display + ':' + (minutes < 10 ? '0' : '') + minutes + ' ' + meridiem;
  }

  /** "August 10" / "August 10, 2027" once outside the current year. */
  function formatDate(date, includeYear) {
    var out = MONTHS[date.getMonth()] + ' ' + date.getDate();
    return includeYear ? out + ', ' + date.getFullYear() : out;
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function unique(values) {
    var seen = Object.create(null);
    var out = [];
    values.forEach(function (value) {
      if (!value || seen[value]) return;
      seen[value] = true;
      out.push(value);
    });
    return out;
  }

  function SexyhairEvents(root) {
    this.root = root;
    this.viewport = root.querySelector('[data-event-viewport]');
    this.rangeLabel = root.querySelector('[data-event-range]');
    this.searchInput = root.querySelector('[data-event-search]');

    this.view = 'list'; // live defaults to List
    this.search = '';
    this.selected = { category: [], venue: [], state: [] };
    this.page = 0;

    var today = startOfDay(new Date());
    this.today = today;
    /** List view anchors at "now"; Month view at the current month. */
    this.listAnchor = today;
    this.monthAnchor = new Date(today.getFullYear(), today.getMonth(), 1);

    this.events = this.readEvents();
    this.buildFilters();
    this.bind();
    this.render();
  }

  SexyhairEvents.prototype.readEvents = function () {
    var node = this.root.querySelector('[data-events]');
    var raw = [];
    try {
      raw = JSON.parse(node.textContent) || [];
    } catch (_error) {
      // A malformed payload renders the empty state rather than throwing.
      raw = [];
    }

    return raw
      .map(function (item) {
        var start = parseLocal(item.localStart);
        if (!start) return null;
        return {
          title: item.title || '',
          url: item.url || '',
          image: item.image || null,
          start: start,
          end: parseLocal(item.localEnd) || start,
          tzAbbr: item.tzAbbr || '',
          startsAt: Number(item.startsAt) || 0,
          endsAt: Number(item.endsAt) || 0,
          venue: item.venue || '',
          address: item.address || '',
          city: item.city || '',
          state: item.state || '',
          zip: item.zip || '',
          formatLabel: item.formatLabel || '',
          excerpt: item.excerpt || '',
          categories: item.categories || [],
        };
      })
      .filter(Boolean)
      .sort(function (a, b) {
        return a.start - b.start;
      });
    // Sorted HERE, in JS. Liquid must never `sort` metaobjects.event.values — on that
    // lazy drop the filter silently returns an empty list. See the snippet header.
  };

  /** Populate the three filter panels from the data actually present. */
  SexyhairEvents.prototype.buildFilters = function () {
    var self = this;
    var sources = {
      category: unique(
        this.events.reduce(function (acc, event) {
          return acc.concat(event.categories);
        }, [])
      ).sort(),
      venue: unique(
        this.events.map(function (event) {
          return event.venue;
        })
      ).sort(),
      state: unique(
        this.events.map(function (event) {
          return event.state;
        })
      ).sort(),
    };

    Object.keys(sources).forEach(function (key) {
      var wrap = self.root.querySelector('[data-event-filter="' + key + '"]');
      if (!wrap) return;
      var panel = wrap.querySelector('[data-event-filter-panel]');
      var values = sources[key];

      if (!values.length) {
        wrap.hidden = true;
        return;
      }

      panel.innerHTML = values
        .map(function (value) {
          return (
            '<label class="sexyhair-events__filter-option">' +
            '<input type="checkbox" value="' +
            escapeHtml(value) +
            '">' +
            '<span>' +
            escapeHtml(value) +
            '</span>' +
            '</label>'
          );
        })
        .join('');
    });
  };

  SexyhairEvents.prototype.bind = function () {
    var self = this;

    this.root.addEventListener('click', function (event) {
      var viewBtn = event.target.closest('[data-event-view]');
      if (viewBtn) {
        self.setView(viewBtn.getAttribute('data-event-view'));
        return;
      }

      var navBtn = event.target.closest('[data-event-nav]');
      if (navBtn) {
        self.navigate(navBtn.getAttribute('data-event-nav'));
        return;
      }

      var pageBtn = event.target.closest('[data-event-page]');
      if (pageBtn) {
        self.paginate(pageBtn.getAttribute('data-event-page'));
        return;
      }

      var findBtn = event.target.closest('[data-event-find]');
      if (findBtn) {
        self.applySearch();
        return;
      }

      var filterToggle = event.target.closest('[data-event-filter-toggle]');
      if (filterToggle) {
        self.toggleFilters(filterToggle);
        return;
      }

      var pill = event.target.closest('.sexyhair-events__filter-pill');
      if (pill) {
        self.togglePanel(pill);
        return;
      }

      var subscribeToggle = event.target.closest('.sexyhair-events__subscribe-toggle');
      if (subscribeToggle) {
        self.toggleSubscribe(subscribeToggle);
        return;
      }

      var more = event.target.closest('[data-event-more]');
      if (more) {
        // "+ N More" reveals the rest of that day's cell in place.
        var cell = more.closest('.sexyhair-events__cell');
        if (cell) {
          cell.classList.add('is-expanded');
          more.remove();
        }
        return;
      }

      // Any click outside an open dropdown closes it.
      if (!event.target.closest('[data-event-filter]')) self.closePanels();
      if (!event.target.closest('[data-event-subscribe]')) self.closeSubscribe();
    });

    this.root.addEventListener('change', function (event) {
      if (event.target.matches('[data-event-filter-panel] input')) self.readFilters();
    });

    if (this.searchInput) {
      this.searchInput.addEventListener('input', function () {
        self.applySearch();
      });
      this.searchInput.addEventListener('keydown', function (event) {
        // The button is decorative once input filters live; still stop the Enter
        // key from submitting any ancestor form.
        if (event.key === 'Enter') {
          event.preventDefault();
          self.applySearch();
        }
      });
    }

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') {
        self.closePanels();
        self.closeSubscribe();
      }
    });
  };

  SexyhairEvents.prototype.applySearch = function () {
    this.search = (this.searchInput ? this.searchInput.value : '').trim().toLowerCase();
    this.page = 0;
    this.render();
  };

  SexyhairEvents.prototype.readFilters = function () {
    var self = this;
    ['category', 'venue', 'state'].forEach(function (key) {
      var wrap = self.root.querySelector('[data-event-filter="' + key + '"]');
      if (!wrap) return;
      var checked = Array.prototype.slice
        .call(wrap.querySelectorAll('input:checked'))
        .map(function (input) {
          return input.value;
        });
      self.selected[key] = checked;

      var label = wrap.querySelector('[data-event-filter-label]');
      var base = { category: 'Event Category', venue: 'Venues', state: 'State/Province' }[key];
      if (label) label.textContent = checked.length ? base + ' (' + checked.length + ')' : base;
      wrap.classList.toggle('is-filtered', checked.length > 0);
    });
    this.page = 0;
    this.render();
  };

  SexyhairEvents.prototype.toggleFilters = function (button) {
    var panel = this.root.querySelector('[data-event-filters]');
    var hidden = panel.hasAttribute('hidden');
    if (hidden) {
      panel.removeAttribute('hidden');
    } else {
      panel.setAttribute('hidden', '');
    }
    button.setAttribute('aria-expanded', hidden ? 'true' : 'false');
    var label = button.querySelector('[data-event-filter-toggle-label]');
    if (label) label.textContent = hidden ? 'Hide Filters' : 'Show Filters';
  };

  SexyhairEvents.prototype.togglePanel = function (pill) {
    var wrap = pill.closest('[data-event-filter]');
    var panel = wrap.querySelector('[data-event-filter-panel]');
    var willOpen = panel.hasAttribute('hidden');
    this.closePanels();
    if (willOpen) {
      panel.removeAttribute('hidden');
      pill.setAttribute('aria-expanded', 'true');
    }
  };

  SexyhairEvents.prototype.closePanels = function () {
    Array.prototype.slice
      .call(this.root.querySelectorAll('[data-event-filter-panel]'))
      .forEach(function (panel) {
        panel.setAttribute('hidden', '');
      });
    Array.prototype.slice
      .call(this.root.querySelectorAll('.sexyhair-events__filter-pill'))
      .forEach(function (pill) {
        pill.setAttribute('aria-expanded', 'false');
      });
  };

  SexyhairEvents.prototype.toggleSubscribe = function (button) {
    var panel = this.root.querySelector('[data-event-subscribe-panel]');
    var willOpen = panel.hasAttribute('hidden');
    if (willOpen) {
      panel.removeAttribute('hidden');
      button.setAttribute('aria-expanded', 'true');
    } else {
      this.closeSubscribe();
    }
  };

  SexyhairEvents.prototype.closeSubscribe = function () {
    var panel = this.root.querySelector('[data-event-subscribe-panel]');
    var button = this.root.querySelector('.sexyhair-events__subscribe-toggle');
    if (panel) panel.setAttribute('hidden', '');
    if (button) button.setAttribute('aria-expanded', 'false');
  };

  SexyhairEvents.prototype.setView = function (view) {
    if (view === this.view) return;
    this.view = view;
    this.page = 0;
    Array.prototype.slice
      .call(this.root.querySelectorAll('[data-event-view]'))
      .forEach(function (button) {
        var active = button.getAttribute('data-event-view') === view;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    this.render();
  };

  SexyhairEvents.prototype.navigate = function (direction) {
    if (direction === 'today') {
      this.listAnchor = this.today;
      this.monthAnchor = new Date(this.today.getFullYear(), this.today.getMonth(), 1);
      this.page = 0;
      this.render();
      return;
    }

    var step = direction === 'next' ? 1 : -1;
    if (this.view === 'month') {
      this.monthAnchor = new Date(
        this.monthAnchor.getFullYear(),
        this.monthAnchor.getMonth() + step,
        1
      );
    } else {
      this.paginate(direction);
      return;
    }
    this.render();
  };

  SexyhairEvents.prototype.paginate = function (direction) {
    if (this.view === 'month') {
      this.navigate(direction);
      return;
    }
    var next = this.page + (direction === 'next' ? 1 : -1);
    if (next < 0) return;
    // Bound against the SAME set renderList paginates (upcoming), not `filtered()`.
    // The date-nav arrows route here and are never disabled, so measuring the
    // unfiltered set would let them page past the end into an empty view as soon
    // as any event falls into the past.
    var total = this.upcoming().length;
    if (next * PAGE_SIZE >= total && total > 0) return;
    this.page = next;
    this.render();
  };

  /** Search + the three facet groups. Date windowing is per-view, applied after. */
  SexyhairEvents.prototype.filtered = function () {
    var self = this;
    return this.events.filter(function (event) {
      if (self.search) {
        var haystack = (
          event.title +
          ' ' +
          event.venue +
          ' ' +
          event.city +
          ' ' +
          event.state +
          ' ' +
          event.excerpt +
          ' ' +
          event.categories.join(' ')
        ).toLowerCase();
        if (haystack.indexOf(self.search) === -1) return false;
      }
      if (self.selected.category.length) {
        var hit = event.categories.some(function (category) {
          return self.selected.category.indexOf(category) !== -1;
        });
        if (!hit) return false;
      }
      if (self.selected.venue.length && self.selected.venue.indexOf(event.venue) === -1) {
        return false;
      }
      if (self.selected.state.length && self.selected.state.indexOf(event.state) === -1) {
        return false;
      }
      return true;
    });
  };

  /**
   * The List view's working set: matches the live site, whose default range starts
   * at "now" — an event drops out once it has ENDED, so a class running right now
   * still shows. Past events keep their detail page (with a "has passed" notice)
   * and still appear when browsing back to their month in Month view.
   */
  SexyhairEvents.prototype.upcoming = function () {
    var anchor = this.listAnchor;
    return this.filtered().filter(function (event) {
      return startOfDay(event.end) >= anchor;
    });
  };

  SexyhairEvents.prototype.render = function () {
    if (this.view === 'month') {
      this.renderMonth();
    } else {
      this.renderList();
    }
  };

  // -------------------------------------------------------------------------
  // List view
  // -------------------------------------------------------------------------

  SexyhairEvents.prototype.renderList = function () {
    var self = this;
    var upcoming = this.upcoming();

    var slice = upcoming.slice(this.page * PAGE_SIZE, this.page * PAGE_SIZE + PAGE_SIZE);

    if (!slice.length) {
      this.viewport.innerHTML =
        '<p class="sexyhair-events__empty type-body">There were no results found.</p>';
      this.renderRangeLabel(null, null);
      this.updatePager(upcoming.length);
      // Rebuild from the (empty) set too, so the .ics link never keeps a stale blob
      // from a previous filter state.
      this.buildIcs(upcoming);
      return;
    }

    var html = '';
    var currentMonth = '';
    slice.forEach(function (event) {
      var monthLabel = MONTHS[event.start.getMonth()] + ' ' + event.start.getFullYear();
      if (monthLabel !== currentMonth) {
        currentMonth = monthLabel;
        html +=
          '<h2 class="sexyhair-events__month-heading"><span>' +
          escapeHtml(monthLabel) +
          '</span></h2>';
      }
      html += self.listRow(event);
    });

    this.viewport.innerHTML = '<div class="sexyhair-events__list">' + html + '</div>';
    this.renderRangeLabel(slice[0].start, slice[slice.length - 1].start);
    this.updatePager(upcoming.length);
    this.buildIcs(upcoming);
  };

  SexyhairEvents.prototype.listRow = function (event) {
    var showYear = event.start.getFullYear() !== this.today.getFullYear();
    var time =
      formatDate(event.start, showYear) +
      ' @ ' +
      formatTime(event.start) +
      ' - ' +
      formatTime(event.end) +
      (event.tzAbbr ? ' ' + escapeHtml(event.tzAbbr) : '');

    var locationParts = [event.address, event.city, event.state, 'United States'].filter(Boolean);
    var venueHtml = '';
    if (event.venue) {
      venueHtml =
        '<p class="sexyhair-events__venue">' +
        '<span class="sexyhair-events__venue-name">' +
        escapeHtml(event.venue) +
        '</span> ' +
        escapeHtml(locationParts.join(', ')) +
        '</p>';
    }

    var titleHtml = event.url
      ? '<a href="' + escapeHtml(event.url) + '">' + escapeHtml(event.title) + '</a>'
      : escapeHtml(event.title);

    // The thumbnail is only a link when there is somewhere to go. `event.url` is
    // blank whenever the metaobject has no resolvable Online Store URL (a definition
    // missing the onlineStore/renderable capabilities, or a draft entry), and an
    // `href=""` is not an inert link — it re-navigates to the current page.
    var imgTag =
      '<img src="' +
      escapeHtml(event.image) +
      '" alt="" loading="lazy" decoding="async" width="600" height="300">';
    var imageHtml = event.image
      ? '<div class="sexyhair-events__row-media">' +
        (event.url
          ? '<a href="' +
            escapeHtml(event.url) +
            '" tabindex="-1" aria-hidden="true">' +
            imgTag +
            '</a>'
          : imgTag) +
        '</div>'
      : '';

    return (
      '<article class="sexyhair-events__row">' +
      '<div class="sexyhair-events__row-date">' +
      '<span class="sexyhair-events__row-day">' +
      DAYS_SHORT[event.start.getDay()] +
      '</span>' +
      '<span class="sexyhair-events__row-num">' +
      event.start.getDate() +
      '</span>' +
      '</div>' +
      '<div class="sexyhair-events__row-body">' +
      '<p class="sexyhair-events__row-time">' +
      time +
      '</p>' +
      '<h3 class="sexyhair-events__row-title">' +
      titleHtml +
      '</h3>' +
      venueHtml +
      (event.excerpt
        ? '<p class="sexyhair-events__row-excerpt">' + escapeHtml(event.excerpt) + '</p>'
        : '') +
      '</div>' +
      imageHtml +
      '</article>'
    );
  };

  SexyhairEvents.prototype.renderRangeLabel = function (from, to) {
    if (!this.rangeLabel) return;
    if (this.view === 'month') {
      this.rangeLabel.textContent =
        MONTHS[this.monthAnchor.getMonth()] + ' ' + this.monthAnchor.getFullYear();
      return;
    }
    if (!from || !to) {
      this.rangeLabel.textContent = 'Now';
      return;
    }
    var showYear = to.getFullYear() !== this.today.getFullYear();
    // Page 1 opens at "Now" exactly like the live range picker.
    var left = this.page === 0 ? 'Now' : formatDate(from, showYear);
    this.rangeLabel.textContent = left + ' - ' + formatDate(to, showYear);
  };

  SexyhairEvents.prototype.updatePager = function (total) {
    var prev = this.root.querySelector('[data-event-page="prev"]');
    var next = this.root.querySelector('[data-event-page="next"]');
    if (this.view === 'month') {
      if (prev) prev.disabled = false;
      if (next) next.disabled = false;
      return;
    }
    if (prev) prev.disabled = this.page === 0;
    if (next) next.disabled = (this.page + 1) * PAGE_SIZE >= total;
  };

  // -------------------------------------------------------------------------
  // Month view
  // -------------------------------------------------------------------------

  SexyhairEvents.prototype.renderMonth = function () {
    var events = this.filtered();
    var year = this.monthAnchor.getFullYear();
    var month = this.monthAnchor.getMonth();

    var first = new Date(year, month, 1);
    // Monday-first grid: JS getDay() is Sunday-based, so Sunday (0) maps to index 6.
    var leading = (first.getDay() + 6) % 7;
    var gridStart = new Date(year, month, 1 - leading);

    // Round up to whole weeks. Breaking out of a fixed 42-cell loop the moment the
    // date leaves the month ends the grid mid-row — a month like August 2026 needs
    // 36 cells, leaving a final row of 1 cell and 6 empty gaps.
    var daysInMonth = new Date(year, month + 1, 0).getDate();
    var totalCells = Math.ceil((leading + daysInMonth) / 7) * 7;

    var html =
      '<div class="sexyhair-events__weekdays" aria-hidden="true">' +
      WEEKDAY_INITIALS.map(function (initial) {
        return '<span>' + initial + '</span>';
      }).join('') +
      '</div><div class="sexyhair-events__grid">';

    for (var i = 0; i < totalCells; i++) {
      var day = new Date(gridStart.getFullYear(), gridStart.getMonth(), gridStart.getDate() + i);

      var dayEvents = events.filter(function (event) {
        return sameDay(event.start, day);
      });

      var classes = 'sexyhair-events__cell';
      if (day.getMonth() !== month) classes += ' sexyhair-events__cell--outside';
      if (sameDay(day, this.today)) classes += ' sexyhair-events__cell--today';

      html +=
        '<div class="' +
        classes +
        '"><span class="sexyhair-events__cell-num">' +
        day.getDate() +
        '</span>';

      dayEvents.forEach(function (event, index) {
        html +=
          '<div class="sexyhair-events__cell-event' +
          (index >= CELL_LIMIT ? ' sexyhair-events__cell-event--overflow' : '') +
          '">' +
          '<span class="sexyhair-events__cell-time">' +
          formatTime(event.start) +
          ' - ' +
          formatTime(event.end) +
          '</span>' +
          // Same rule as the list row: plain text when there is no URL, rather than
          // an anchor with an empty href that silently reloads the page.
          (event.url
            ? '<a class="sexyhair-events__cell-title" href="' +
              escapeHtml(event.url) +
              '">' +
              escapeHtml(event.title) +
              '</a>'
            : '<span class="sexyhair-events__cell-title">' +
              escapeHtml(event.title) +
              '</span>') +
          '</div>';
      });

      if (dayEvents.length > CELL_LIMIT) {
        html +=
          '<button type="button" class="sexyhair-events__cell-more" data-event-more>+ ' +
          (dayEvents.length - CELL_LIMIT) +
          ' More</button>';
      }

      html += '</div>';
    }

    this.viewport.innerHTML = html + '</div>';
    this.renderRangeLabel(null, null);
    this.updatePager(events.length);
    this.buildIcs(events);
  };

  // -------------------------------------------------------------------------
  // Subscribe / .ics
  // -------------------------------------------------------------------------

  /**
   * Build a downloadable .ics from the UTC epochs (NOT the wall-clock fields —
   * a calendar file must carry the real instant so each subscriber's client can
   * localise it).
   */
  SexyhairEvents.prototype.buildIcs = function (events) {
    var link = this.root.querySelector('[data-event-ics]');
    if (!link) return;

    function stamp(epochSeconds) {
      return new Date(epochSeconds * 1000).toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
    }

    function fold(value) {
      return String(value).replace(/([,;\\])/g, '\\$1').replace(/\r?\n/g, '\\n');
    }

    var lines = ['BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//SexyHair//Events//EN', 'CALSCALE:GREGORIAN'];
    events.forEach(function (event) {
      if (!event.startsAt) return;
      lines.push(
        'BEGIN:VEVENT',
        'UID:' + fold(event.url || event.title),
        'DTSTART:' + stamp(event.startsAt),
        'DTEND:' + stamp(event.endsAt || event.startsAt),
        'SUMMARY:' + fold(event.title),
        'LOCATION:' +
          fold([event.venue, event.address, event.city, event.state, event.zip].filter(Boolean).join(', ')),
        'URL:' + fold(event.url),
        'END:VEVENT'
      );
    });
    lines.push('END:VCALENDAR');

    if (this.icsUrl) URL.revokeObjectURL(this.icsUrl);
    this.icsUrl = URL.createObjectURL(
      new Blob([lines.join('\r\n')], { type: 'text/calendar;charset=utf-8' })
    );
    link.href = this.icsUrl;
  };

  function init() {
    Array.prototype.slice
      .call(document.querySelectorAll('[data-sexyhair-events]'))
      .forEach(function (root) {
        if (root.dataset.eventsReady) return;
        root.dataset.eventsReady = 'true';
        new SexyhairEvents(root);
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
